<template>
	<div
		v-if="showCarousel"
		:class="['w-full', hasGradientBg ? 'bg-gradient-to-t from-[#F3F7FD]' : '']"
		data-path="JobsPageCarousel"
	>
		<!-- Heading -->
		<AdaptiveContainer>
			<div class="flex items-baseline col-span-full mt-4 sm:mt-16 mb-8">
				<BaseHeading
					type="h5"
					class="mt-5 mb-2 relative before:content-['&nbsp;'] before:block before:absolute before:-bottom-1 before:h-1 before:w-20 before:bg-red-500"
				>
					{{ title }}
				</BaseHeading>
				<BaseHeading
					v-if="rawJobsData.length != 0"
					type="h6"
					class="hidden md:block font-normal mt-2 sm:mt-0 sm:ml-6 text-lg text-dj-gray"
				>
					<!-- {{ $t("jobs.allOpenJobs", { msg: rawJobsData.length }) }} -->
				</BaseHeading>
				<NuxtLink
					v-if="!hideAllJobs"
					:to="allJobsPath"
					@click="fetchData"
					class="hidden sm:flex items-center font-medium text-sm ml-auto cursor-pointer"
				>
					<span class="mr-2">{{ $t("jobs.allJobs") }}</span>
					<BaseIcon icon="ic:outline-arrow-forward" :width="16" :height="16" />
				</NuxtLink>
			</div>
		</AdaptiveContainer>

		<!-- Loading state -->
		<AdaptiveContainer>
			<template v-if="isLoading && ssrFallbackJobs.length === 0">
				<div v-for="item in 3" :key="item" class="inline-block float-left w-[320px] mr-10 mt-4">
					<DjCardJob :loading="isLoading" />
				</div>
			</template>
		</AdaptiveContainer>

		<!-- Carousel -->
		<CarouselJobs
			:data-url="carouselDataUrl"
			:slideWidth="320"
			@loaded="isLoading = false"
			:highlightedGold="highlightedGold"
			@hide-section="showCarousel = false"
			:rawJobsData="rawJobsForCarousel"
			:filterJobId="filterJobId"
			:sortByGold="sortByGold"
			:bannerType="carouselType"
		></CarouselJobs>

		<!-- Mobile view -->
		<div class="sm:hidden mt-8 mb-6" v-if="!hideAllJobs">
			<NuxtLink
				:to="allJobsPath"
				@click="fetchData"
				class="w-full flex justify-center items-center bg-dj-blue/5 text-sm text-dj-blue hover:bg-dj-blue/10 active:bg-dj-blue/10 max-w-[320px] mx-auto mb-4 p-3 rounded-normal"
			>
				<span class="mr-2">{{ $t("jobs.allJobs") }}</span>
				<BaseIcon icon="ic:outline-arrow-forward" :width="16" :height="16" />
			</NuxtLink>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

type JobCardItem = {
	id?: number;
	company?: {
		has_gold?: number;
	};
	[key: string]: unknown;
};

const { locale } = useI18n();

const props = defineProps({
	carouselDataUrl: {
		type: String,
		default: "jobs",
	},
	dataUrl: {
		type: String,
		default: "",
	},
	title: {
		type: String,
		default: "Carousel title",
	},
	hasGradientBg: {
		type: Boolean,
		default: false,
	},
	highlightedGold: {
		type: Boolean,
		default: false,
	},
	routeParam: {
		type: String,
		default: "",
	},
	rawJobsData: {
		type: Array,
		default: () => [],
	},
	hideAllJobs: {
		type: Boolean,
		default: false,
	},
	filterJobId: {
		type: Number,
		default: null,
	},
	sortByGold: {
		type: Boolean,
		default: false,
	},
	carouselType: {
		type: String,
		default: "",
	},
});

const emit = defineEmits(["dataFetched"]);

const isLoading = ref(true);
const showCarousel = ref(true);

const rawJobs = computed<JobCardItem[]>(() => props.rawJobsData as JobCardItem[]);

const ssrDataUrl = computed(() => {
	if (rawJobs.value.length > 0 || !props.dataUrl) {
		return null;
	}

	let url = props.dataUrl;
	if (!url.includes("region=")) {
		const separator = url.includes("?") ? "&" : "?";
		url += `${separator}region=${useRegion.getCurrentRegion().code.toLowerCase()}`;
	}

	if (!url.includes("limit=")) {
		const separator = url.includes("?") ? "&" : "?";
		url += `${separator}limit=20`;
	}

	return url;
});

const { data: ssrResponse } = await useFetch<{ data: JobCardItem[] }>(() => ssrDataUrl.value as string, {
	server: true,
	immediate: !!ssrDataUrl.value, // Only run the fetch if we actually have a URL
	key: props.carouselType,
	default: () => ({ data: [] }),
});

function getGoldValue(job: JobCardItem): number {
	return Number(job.company?.has_gold ?? 0);
}

const ssrFallbackJobs = computed<JobCardItem[]>(() => {
	let jobs = rawJobs.value.length > 0 ? [...rawJobs.value] : [...(ssrResponse.value?.data || [])];

	if (props.filterJobId) {
		jobs = jobs.filter((job) => job.id !== props.filterJobId);
	}

	if (props.sortByGold) {
		jobs = jobs.sort((a, b) => getGoldValue(b) - getGoldValue(a));
	}

	return jobs.slice(0, 6);
});

const rawJobsForCarousel = computed<JobCardItem[]>(() => {
	return rawJobs.value.length > 0 ? rawJobs.value : ssrFallbackJobs.value;
});

const allJobsPath = computed(() => {
	const path = `/${locale.value}/jobs`;
	if (!props.routeParam) {
		return path;
	}

	return {
		path,
		query: {
			[props.routeParam]: "true",
		},
	};
});

function fetchData() {
	emit("dataFetched", { url: props.dataUrl, param: props.routeParam });
}
</script>

<script lang="ts">
export default {
	name: "JobsPageCarousel",
};
</script>
